import React, { useEffect, useState } from 'react';
import { MapPin, Search, PlusCircle, LogOut, User as UserIcon, Shield, ChevronDown, Bug } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useBugReport } from '@/context/BugReportContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { farmService } from '@/services/farmService';
import { FarmRecord } from '@/types/database';
import { ActionTooltip } from '@/components/common/ActionTooltip';

interface DesktopHeaderProps {
  onOpenRecordActivity: () => void;
  selectedFarmId: string | null;
  onSelectFarm: (farmId: string | null) => void;
}

export const DesktopHeader: React.FC<DesktopHeaderProps> = ({
  onOpenRecordActivity,
  selectedFarmId,
  onSelectFarm,
}) => {
  const { user, profile, role, isOperations, signOut } = useAuth();
  const { openBugReport } = useBugReport();
  const [farms, setFarms] = useState<FarmRecord[]>([]);

  useEffect(() => {
    farmService.getActiveFarms().then((data) => {
      setFarms(data);
    });
  }, []);

  const currentFarmName = selectedFarmId
    ? farms.find((f) => f.id === selectedFarmId)?.farm_name || 'Selected Farm'
    : 'All Farms';

  return (
    <header className="hidden lg:flex h-16 items-center justify-between px-6 bg-white border-b border-slate-200 sticky top-0 z-20 shadow-xs">
      {/* Left: Farm Location Switcher */}
      <div className="flex items-center gap-4">
        <DropdownMenu>
          <ActionTooltip content="Filter active workspace by farm location.">
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-slate-300 bg-slate-50 text-xs font-semibold text-slate-800 transition-colors">
                <MapPin className="w-3.5 h-3.5 text-emerald-700" />
                <span>Location:</span>
                <span className="text-emerald-800 font-bold max-w-[180px] truncate">{currentFarmName}</span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
          </ActionTooltip>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Filter by Farm</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => onSelectFarm(null)}
              className={selectedFarmId === null ? 'font-bold text-emerald-800 bg-emerald-50' : ''}
            >
              📍 All Farms
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {farms.map((farm) => (
              <DropdownMenuItem
                key={farm.id}
                onClick={() => onSelectFarm(farm.id)}
                className={selectedFarmId === farm.id ? 'font-bold text-emerald-800 bg-emerald-50' : ''}
              >
                {farm.farm_name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Global Search Bar mockup */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search cycles, growers, tickets (Ctrl+K)..."
            className="w-64 pl-9 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-slate-50 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-600 focus:bg-white transition-all"
          />
        </div>
      </div>

      {/* Right: Bug Report, Record Activity & User Profile */}
      <div className="flex items-center gap-3">
        {/* Report a Bug quick button */}
        <ActionTooltip content="Report a problem or bug on this screen.">
          <button
            onClick={() => openBugReport()}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:border-red-200 bg-slate-50 hover:bg-red-50 text-xs font-semibold text-slate-700 hover:text-red-700 transition-colors"
            title="Report a Bug"
          >
            <Bug className="w-3.5 h-3.5 text-red-500" />
            <span className="hidden xl:inline">Report a Bug</span>
          </button>
        </ActionTooltip>

        {isOperations && (
          <ActionTooltip content="Open 9-tile quick form launcher for arrivals, mortality, feed, sales, and expenses.">
            <Button
              onClick={onOpenRecordActivity}
              variant="default"
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-600 font-semibold gap-1.5 shadow-sm"
            >
              <PlusCircle className="w-4 h-4" /> Record Activity
            </Button>
          </ActionTooltip>
        )}

        <DropdownMenu>
          <ActionTooltip content="View active account profile, role, and sign-out options.">
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-left">
                <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-xs">
                  {profile?.full_name?.charAt(0) || user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="hidden xl:block text-xs">
                  <p className="font-semibold text-slate-900 truncate max-w-[120px]">
                    {profile?.full_name || user?.email}
                  </p>
                  <div className="flex items-center gap-1 text-[10px] text-slate-500 uppercase font-bold">
                    <Shield className="w-2.5 h-2.5 text-emerald-700" />
                    {role || 'Viewer'}
                  </div>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
          </ActionTooltip>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="text-xs font-semibold text-slate-900">{profile?.full_name || 'User Account'}</p>
              <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-700">
                Role: {role || 'Viewer'}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => openBugReport()} className="text-slate-700 hover:text-red-700 cursor-pointer">
              <Bug className="w-4 h-4 mr-2 text-red-500" /> Report a Bug
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut} className="text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

